import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, XCircle, Loader2 } from 'lucide-react';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
}

interface QuizModalProps {
  isOpen: boolean;
  onClose: () => void;
  questions: QuizQuestion[];
  loading: boolean;
}

export function QuizModal({ isOpen, onClose, questions, loading }: QuizModalProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = answerIndex;
    setSelectedAnswers(newAnswers);
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach((q, i) => {
      if (selectedAnswers[i] === q.correctAnswer) correct++;
    });
    return Math.round((correct / questions.length) * 100);
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers([]);
    setShowResults(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 flex items-center justify-center px-4 py-6 sm:p-4 z-50 overflow-y-auto"
          >
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl mx-auto relative my-auto">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Quick Knowledge Check</h2>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    <span className="ml-3 text-gray-600">Generating quiz questions...</span>
                  </div>
                ) : showResults ? (
                  <div className="space-y-6">
                    <div className="text-center">
                      <div className="text-3xl sm:text-4xl font-bold mb-2">
                        {calculateScore()}% {calculateScore() >= 80 ? '🎉' : ''}
                      </div>
                      <p className="text-gray-600">
                        You got {selectedAnswers.filter((a, i) => a === questions[i].correctAnswer).length} out of {questions.length} questions correct
                      </p>
                    </div>

                    <div className="space-y-3 sm:space-y-4">
                      {questions.map((q, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-3 sm:p-4">
                          <div className="flex items-start gap-3">
                            {selectedAnswers[i] === q.correctAnswer ? (
                              <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-600 mt-1" />
                            )}
                            <div>
                              <p className="text-sm sm:text-base font-medium text-gray-900 mb-2">{q.question}</p>
                              <div className="space-y-1.5 sm:space-y-2">
                                {q.options.map((option, j) => (
                                  <div
                                    key={j}
                                    className={`p-2 rounded ${
                                      j === q.correctAnswer
                                        ? 'bg-green-100 text-green-700 font-medium'
                                        : j === selectedAnswers[i]
                                        ? 'bg-red-100 text-red-700'
                                        : 'bg-gray-100 text-gray-700'
                                    } text-sm sm:text-base`}
                                  >
                                    {option}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        onClick={resetQuiz}
                        className="px-3 sm:px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 text-sm sm:text-base"
                      >
                        Try Again
                      </button>
                      <button
                        onClick={onClose}
                        className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div className="text-sm text-gray-600">
                        {currentQuestion + 1}/{questions.length}
                      </div>
                      <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 transition-all duration-300"
                          style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                        />
                      </div>
                    </div>

                    <div className="mb-8">
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">
                        {questions[currentQuestion].question}
                      </h3>
                      <div className="space-y-2 sm:space-y-3">
                        {questions[currentQuestion].options.map((option, i) => (
                          <button
                            key={i}
                            onClick={() => handleAnswerSelect(i)}
                            className={`w-full p-3 text-left rounded-lg transition-colors duration-200 ${
                              selectedAnswers[currentQuestion] === i
                                ? 'bg-blue-100 text-blue-700 border-2 border-blue-500 font-medium'
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-transparent'
                            } text-sm sm:text-base`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <button
                        onClick={() => setCurrentQuestion(prev => prev - 1)}
                        disabled={currentQuestion === 0}
                        className="px-3 sm:px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:hover:bg-transparent text-sm sm:text-base"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          if (currentQuestion === questions.length - 1) {
                            setShowResults(true);
                          } else {
                            setCurrentQuestion(prev => prev + 1);
                          }
                        }}
                        disabled={selectedAnswers[currentQuestion] === undefined}
                        className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:hover:bg-blue-600 text-sm sm:text-base"
                      >
                        {currentQuestion === questions.length - 1 ? 'Finish' : 'Next'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}